import React, { useState, useEffect } from 'react';
import {
  Drawer, Tabs, Descriptions, Tag, Table, Statistic, Row, Col, Card,
  Timeline, Button, Space, Avatar, Badge as AntBadge, Empty, Spin,
  Modal, Input, Select, message,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
  BankOutlined, UserOutlined, TeamOutlined, CarOutlined,
  DollarOutlined, BarChartOutlined, CreditCardOutlined,
  HistoryOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  TenantDetails, TenantEmployee, TenantOperations,
  TenantFinancials, TenantApiUsage, AuditLog, SubscriptionPlan,
  SUBSCRIPTION_STATUS_COLORS,
} from '../../types/tenant';

const dateFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const dateTimeFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

interface AdminTenantDrawerProps {
  tenant: TenantDetails | null;
  isOpen: boolean;
  onClose: () => void;
  employees: { data: TenantEmployee[]; total: number } | null;
  operations: TenantOperations | null;
  financials: TenantFinancials | null;
  apiUsage: TenantApiUsage | null;
  auditLogs: AuditLog[];
  plans: SubscriptionPlan[];
  onFetchEmployees: (id: number) => void;
  onFetchOperations: (id: number) => void;
  onFetchFinancials: (id: number) => void;
  onFetchApiUsage: (id: number) => void;
  onFetchAuditLogs: (id: number) => void;
  onSuspend: (id: number, reason: string) => void;
  onResume: (id: number) => void;
  onCancel: (id: number, reason: string) => void;
  onChangePlan: (id: number, planSlug: string) => void;
}

const AdminTenantDrawer: React.FC<AdminTenantDrawerProps> = ({
  tenant, isOpen, onClose,
  employees, operations, financials, apiUsage, auditLogs, plans,
  onFetchEmployees, onFetchOperations, onFetchFinancials,
  onFetchApiUsage, onFetchAuditLogs,
  onSuspend, onResume, onCancel, onChangePlan,
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (tenant && isOpen) setActiveTab('overview');
  }, [tenant?.id, isOpen]);

  useEffect(() => {
    if (!tenant || !isOpen) return;
    switch (activeTab) {
      case 'employees': if (!employees) onFetchEmployees(tenant.id); break;
      case 'operations': if (!operations) onFetchOperations(tenant.id); break;
      case 'financials': if (!financials) onFetchFinancials(tenant.id); break;
      case 'apiUsage': if (!apiUsage) onFetchApiUsage(tenant.id); break;
      case 'activity': if (auditLogs.length === 0) onFetchAuditLogs(tenant.id); break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks are stable; data vars are intentionally used as guards only
  }, [activeTab, tenant?.id, isOpen, employees, operations, financials, apiUsage, auditLogs.length]);

  if (!tenant) return null;

  const tabItems = [
    { key: 'overview', label: 'Overview', icon: <BankOutlined />, children: <OverviewTab tenant={tenant} /> },
    { key: 'employees', label: 'Employees', icon: <TeamOutlined />, children: <EmployeesTab employees={employees} /> },
    { key: 'operations', label: 'Operations', icon: <CarOutlined />, children: <OperationsTab operations={operations} /> },
    { key: 'financials', label: 'Financials', icon: <DollarOutlined />, children: <FinancialsTab financials={financials} /> },
    { key: 'apiUsage', label: 'API Usage', icon: <BarChartOutlined />, children: <ApiUsageTab apiUsage={apiUsage} /> },
    {
      key: 'subscription', label: 'Subscription', icon: <CreditCardOutlined />,
      children: <SubscriptionTab tenant={tenant} plans={plans} onSuspend={onSuspend} onResume={onResume} onCancel={onCancel} onChangePlan={onChangePlan} />,
    },
    { key: 'activity', label: 'Activity', icon: <HistoryOutlined />, children: <ActivityTab auditLogs={auditLogs} /> },
  ];

  return (
    <Drawer
      title={
        <Space>
          <Avatar icon={<BankOutlined />} style={{ backgroundColor: '#e6f4ff', color: '#1976D2' }} />
          <div>
            <div style={{ fontWeight: 600 }}>{tenant.name}</div>
            <Space size={4}>
              <Tag color={SUBSCRIPTION_STATUS_COLORS[tenant.subscriptionStatus] || 'default'}>{tenant.subscriptionStatus}</Tag>
              <span style={{ fontSize: 13, color: '#999' }}>{tenant.planName}</span>
            </Space>
          </div>
        </Space>
      }
      open={isOpen}
      onClose={onClose}
      width={700}
      placement="right"
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems.map(t => ({ key: t.key, label: <span>{t.icon} {t.label}</span>, children: t.children }))}
        size="small"
      />
    </Drawer>
  );
};

// ============ Tab Components ============

const OverviewTab: React.FC<{ tenant: TenantDetails }> = ({ tenant }) => (
  <div>
    <Descriptions title="Company Information" column={2} bordered size="small" style={{ marginBottom: 24 }}>
      <Descriptions.Item label="Company">{tenant.name}</Descriptions.Item>
      <Descriptions.Item label="Email">{tenant.contactEmail}</Descriptions.Item>
      <Descriptions.Item label="Contact">{tenant.contactName}</Descriptions.Item>
      <Descriptions.Item label="Phone">{tenant.contactPhone || '-'}</Descriptions.Item>
      <Descriptions.Item label="Address" span={2}>
        {[tenant.address, tenant.city, tenant.state].filter(Boolean).join(', ') || '-'}
      </Descriptions.Item>
      <Descriptions.Item label="Joined">
        {tenant.createdAt ? dateFormat.format(new Date(tenant.createdAt)) : '-'}
      </Descriptions.Item>
      {tenant.taxId && <Descriptions.Item label="Tax ID">{tenant.taxId}</Descriptions.Item>}
      {tenant.mcDot && <Descriptions.Item label="MC/DOT">{tenant.mcDot}</Descriptions.Item>}
    </Descriptions>

    {tenant.boss && (
      <Card size="small" title="Boss Account" style={{ marginBottom: 24 }}>
        <Space>
          <Avatar icon={<SafetyCertificateOutlined />} style={{ backgroundColor: '#e6f4ff', color: '#1976D2' }} />
          <div>
            <div style={{ fontWeight: 500 }}>{tenant.boss.username}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{tenant.boss.email}</div>
          </div>
          <Tag color={tenant.boss.isActive ? 'green' : 'red'}>
            {tenant.boss.isActive ? 'Active' : 'Inactive'}
          </Tag>
        </Space>
      </Card>
    )}

    <Card size="small" title="Team Size">
      <Row gutter={[16, 16]}>
        {tenant.employeeCounts && typeof tenant.employeeCounts === 'object'
          ? Object.entries(tenant.employeeCounts).map(([role, count]) => (
              <Col span={8} key={role}>
                <Statistic title={role} value={count as number} />
              </Col>
            ))
          : <Col span={24}><Empty description="No team data" /></Col>
        }
      </Row>
    </Card>
  </div>
);

const employeeColumns: ColumnsType<TenantEmployee> = [
  {
    title: 'User',
    key: 'user',
    render: (_, emp) => (
      <Space>
        <Avatar size="small" icon={<UserOutlined />} />
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{emp.username}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{emp.email}</div>
        </div>
      </Space>
    ),
  },
  {
    title: 'Role',
    dataIndex: 'role',
    key: 'role',
    width: 120,
    render: (role: string) => <Tag>{role}</Tag>,
  },
  {
    title: 'Title',
    dataIndex: 'jobTitle',
    key: 'jobTitle',
    width: 140,
    render: (t: string) => t || '-',
  },
];

const EmployeesTab: React.FC<{ employees: { data: TenantEmployee[]; total: number } | null }> = ({ employees }) => {
  if (!employees) return <Spin tip="Loading..." />;
  return (
    <Table<TenantEmployee>
      columns={employeeColumns}
      dataSource={employees.data}
      rowKey="id"
      size="small"
      pagination={false}
      footer={() => <span style={{ color: '#999' }}>{employees.total} employees total</span>}
    />
  );
};

const OperationsTab: React.FC<{ operations: TenantOperations | null }> = ({ operations }) => {
  if (!operations) return <Spin tip="Loading..." />;
  const ops = operations as Record<string, unknown>;
  return (
    <Row gutter={[16, 16]}>
      <Col span={12}><Card size="small"><Statistic title="Orders This Month" value={ops.ordersThisMonth as number ?? 0} /></Card></Col>
      <Col span={12}><Card size="small"><Statistic title="Tasks This Month" value={ops.tasksThisMonth as number ?? 0} /></Card></Col>
      <Col span={12}><Card size="small"><Statistic title="Tasks Completed" value={ops.tasksCompletedThisMonth as number ?? 0} /></Card></Col>
      <Col span={12}><Card size="small"><Statistic title="Total Trucks" value={ops.totalTrucks as number ?? 0} /></Card></Col>
      <Col span={12}><Card size="small"><Statistic title="Active Drivers" value={ops.activeDrivers as number ?? 0} /></Card></Col>
    </Row>
  );
};

const FinancialsTab: React.FC<{ financials: TenantFinancials | null }> = ({ financials }) => {
  if (!financials) return <Spin tip="Loading..." />;
  const fin = financials as Record<string, unknown>;
  return (
    <Row gutter={[16, 16]}>
      <Col span={12}>
        <Card size="small">
          <Statistic title="Invoices This Month" value={fin.invoicesThisMonth as number ?? 0} prefix="$" precision={0} />
        </Card>
      </Col>
      <Col span={12}>
        <Card size="small">
          <Statistic title="Invoice Count" value={fin.invoiceCountThisMonth as number ?? 0} />
        </Card>
      </Col>
      <Col span={12}>
        <Card size="small">
          <Statistic title="Paid This Month" value={fin.paidThisMonth as number ?? 0} prefix="$" precision={0} />
        </Card>
      </Col>
    </Row>
  );
};

const ApiUsageTab: React.FC<{ apiUsage: TenantApiUsage | null }> = ({ apiUsage }) => {
  if (!apiUsage) return <Spin tip="Loading..." />;

  const daily = (apiUsage as Record<string, unknown>).daily as Array<Record<string, unknown>> || [];
  const topEndpoints = (apiUsage as Record<string, unknown>).topEndpoints as Array<Record<string, unknown>> || [];

  const dailyColumns: ColumnsType<Record<string, unknown>> = [
    { title: 'Date', dataIndex: 'date', key: 'date', render: (d: string) => d ? dateFormat.format(new Date(d)) : '-' },
    { title: 'Calls', dataIndex: 'totalCalls', key: 'calls', align: 'right', render: (v: number) => (v ?? 0).toLocaleString() },
    { title: 'Avg Time', dataIndex: 'avgResponseTime', key: 'avg', align: 'right', render: (v: number) => `${v ?? 0}ms` },
    { title: 'Errors', dataIndex: 'errorCount', key: 'errors', align: 'right', render: (v: number) => (v ?? 0) > 0 ? <span style={{ color: '#C62828' }}>{v}</span> : '0' },
  ];

  const endpointColumns: ColumnsType<Record<string, unknown>> = [
    { title: 'Endpoint', key: 'endpoint', render: (_, e) => <span><Tag>{e.method as string}</Tag> <code>{e.path as string}</code></span> },
    { title: 'Calls', dataIndex: 'callCount', key: 'calls', align: 'right', render: (v: number) => (v ?? 0).toLocaleString() },
  ];

  return (
    <div>
      <Table
        columns={dailyColumns}
        dataSource={daily.slice(-7)}
        rowKey="date"
        size="small"
        pagination={false}
        title={() => <strong>Daily API Calls (Last 7 Days)</strong>}
        style={{ marginBottom: 24 }}
        locale={{ emptyText: <Empty description="No API usage data yet" /> }}
      />
      <Table
        columns={endpointColumns}
        dataSource={topEndpoints.slice(0, 10)}
        rowKey={(_, i) => String(i)}
        size="small"
        pagination={false}
        title={() => <strong>Top Endpoints</strong>}
        locale={{ emptyText: <Empty description="No endpoint data yet" /> }}
      />
    </div>
  );
};

const SubscriptionTab: React.FC<{
  tenant: TenantDetails;
  plans: SubscriptionPlan[];
  onSuspend: (id: number, reason: string) => void;
  onResume: (id: number) => void;
  onCancel: (id: number, reason: string) => void;
  onChangePlan: (id: number, planSlug: string) => void;
}> = ({ tenant, plans, onSuspend, onResume, onCancel, onChangePlan }) => {
  const handleSuspend = () => {
    let reason = '';
    Modal.confirm({
      title: 'Suspend Tenant',
      icon: <ExclamationCircleOutlined />,
      content: <Input.TextArea placeholder="Enter suspend reason…" rows={3} onChange={(e) => { reason = e.target.value; }} />,
      okText: 'Suspend',
      okButtonProps: { danger: true },
      onOk: () => {
        if (!reason.trim()) { message.warning('A reason is required — it goes in the audit log.'); throw new Error('Reason required'); }
        onSuspend(tenant.id, reason.trim());
      },
    });
  };
  const handleCancel = () => {
    let reason = '';
    Modal.confirm({
      title: 'Cancel Subscription',
      icon: <ExclamationCircleOutlined />,
      content: <Input.TextArea placeholder="Enter cancellation reason…" rows={3} onChange={(e) => { reason = e.target.value; }} />,
      okText: 'Cancel Subscription',
      okButtonProps: { danger: true },
      onOk: () => {
        if (!reason.trim()) { message.warning('A reason is required — it goes in the audit log.'); throw new Error('Reason required'); }
        onCancel(tenant.id, reason.trim());
      },
    });
  };
  const handleChangePlan = () => {
    let selectedPlan = '';
    const planOptions = plans
      .filter(p => p.isActive && p.slug !== tenant.planSlug)
      .map(p => ({ label: `${p.name} ($${p.basePrice}/mo)`, value: p.slug }));
    Modal.confirm({
      title: 'Change Plan',
      content: (
        <Select
          placeholder="Select new plan"
          style={{ width: '100%', marginTop: 8 }}
          options={planOptions}
          onChange={(v) => { selectedPlan = v; }}
        />
      ),
      okText: 'Change Plan',
      onOk: () => {
        if (!selectedPlan) { message.warning('Pick the new plan first.'); throw new Error('Plan required'); }
        onChangePlan(tenant.id, selectedPlan);
      },
    });
  };

  return (
    <div>
      <Descriptions title="Subscription Details" column={2} bordered size="small" style={{ marginBottom: 24 }}>
        <Descriptions.Item label="Plan">{tenant.planName}</Descriptions.Item>
        <Descriptions.Item label="Price">${tenant.basePrice}/mo</Descriptions.Item>
        <Descriptions.Item label="Billing Cycle">{tenant.billingCycle}</Descriptions.Item>
        <Descriptions.Item label="Current Period">
          {tenant.currentPeriodStart && tenant.currentPeriodEnd
            ? `${dateFormat.format(new Date(tenant.currentPeriodStart))} - ${dateFormat.format(new Date(tenant.currentPeriodEnd))}`
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Credits">${tenant.credits}</Descriptions.Item>
        {tenant.trialEndsAt && <Descriptions.Item label="Trial Ends">{dateFormat.format(new Date(tenant.trialEndsAt))}</Descriptions.Item>}
        {tenant.suspendReason && <Descriptions.Item label="Suspend Reason">{tenant.suspendReason}</Descriptions.Item>}
      </Descriptions>

      <Card size="small" title="Plan Limits" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col span={12}><Statistic title="Max Trucks" value={tenant.maxTrucks ?? 'Unlimited'} /></Col>
          <Col span={12}><Statistic title="Max Drivers" value={tenant.maxDrivers ?? 'Unlimited'} /></Col>
          <Col span={12}><Statistic title="Max Dispatchers" value={tenant.maxDispatchers ?? 'Unlimited'} /></Col>
          <Col span={12}><Statistic title="API Calls/Month" value={tenant.maxApiCallsPerMonth?.toLocaleString() ?? 'Unlimited'} /></Col>
        </Row>
      </Card>

      <Card size="small" title="Actions">
        {['cancelled', 'expired'].includes(tenant.subscriptionStatus) ? (
          <span style={{ color: '#999' }}>No actions available for {tenant.subscriptionStatus} subscriptions.</span>
        ) : (
          <Space wrap>
            <Button onClick={handleChangePlan}>Change Plan</Button>
            {(tenant.subscriptionStatus === 'active' || tenant.subscriptionStatus === 'trial') ? (
              <Button danger onClick={handleSuspend}>Suspend</Button>
            ) : tenant.subscriptionStatus === 'suspended' ? (
              <Button type="primary" style={{ background: '#2E7D32' }} onClick={() => onResume(tenant.id)}>Resume</Button>
            ) : null}
            <Button danger onClick={handleCancel}>Cancel Subscription</Button>
          </Space>
        )}
      </Card>
    </div>
  );
};

const ActivityTab: React.FC<{ auditLogs: AuditLog[] }> = ({ auditLogs }) => {
  if (auditLogs.length === 0) return <Empty description="No activity logs yet" />;
  return (
    <Timeline
      items={auditLogs.map((log) => ({
        color: 'blue',
        children: (
          <div>
            <div style={{ fontSize: 13 }}>
              <strong>{log.adminUsername}</strong>{' '}
              <span style={{ color: '#666' }}>{log.action.replace(/_/g, ' ')}</span>
            </div>
            {Object.keys(log.details).length > 0 && (
              <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{JSON.stringify(log.details)}</div>
            )}
            <div style={{ fontSize: 12, color: '#999' }}>{dateTimeFormat.format(new Date(log.createdAt))}</div>
          </div>
        ),
      }))}
    />
  );
};

export default AdminTenantDrawer;
